import { useState, useEffect, useMemo } from 'react';
import { workshopBuilderAPI } from '@/lib/api';
import {
  Sparkles, Heart, Apple, Activity, Zap, ArrowRight, ArrowLeft, Check,
  CheckCircle2, Send, Loader2, ChevronRight, Mail, Phone, User, Building, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

// Map icon names from backend to lucide-react components
const ICON_MAP = { Sparkles, Heart, Apple, Activity, Zap };

function getIcon(name) {
  return ICON_MAP[name] || Sparkles;
}

export default function WorkshopBuilderPage() {
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');

  // Wizard state
  const [step, setStep] = useState(0); // 0=pick, 1=blocks, 2=contact, 3=review, 4=done
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [selections, setSelections] = useState({}); // block_key -> value | [values] | string
  const [customInputs, setCustomInputs] = useState({}); // block_key -> custom string for "anders"
  const [contact, setContact] = useState({ name: '', email: '', phone: '', company: '' });
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Force light mode on this public page — embedbare pagina's moeten voorspelbaar zijn
  // ongeacht de CRM theme-voorkeur in localStorage.
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  useEffect(() => {
    workshopBuilderAPI.listPublic()
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => setError('Er ging iets fout bij het laden. Probeer het later opnieuw.'))
      .finally(() => setLoadingList(false));
  }, []);

  const totalSteps = useMemo(() => {
    if (!selectedTpl) return 4;
    return 4; // pick(0) handled separately; show progress over blocks+contact+review
  }, [selectedTpl]);

  const handlePickTemplate = (tpl) => {
    setSelectedTpl(tpl);
    setSelections({});
    setCustomInputs({});
    setStep(1);
  };

  const setSelection = (key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
  };

  const toggleMulti = (key, value) => {
    setSelections(prev => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      if (current.includes(value)) {
        return { ...prev, [key]: current.filter(v => v !== value) };
      }
      return { ...prev, [key]: [...current, value] };
    });
  };

  const validateBlocks = () => {
    for (const block of selectedTpl?.blocks || []) {
      if (!block.required) continue;
      const v = selections[block.key];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        return `Vul "${block.label}" in om verder te gaan.`;
      }
    }
    return '';
  };

  const validateContact = () => {
    if (!contact.name.trim()) return 'Naam is verplicht.';
    if (!contact.email.trim()) return 'E-mailadres is verplicht.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return 'Vul een geldig e-mailadres in.';
    return '';
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      const err = validateBlocks();
      if (err) { setError(err); return; }
    }
    if (step === 2) {
      const err = validateContact();
      if (err) { setError(err); return; }
    }
    setStep(s => s + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(s => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      // Merge custom inputs into selections where applicable
      const finalSelections = { ...selections };
      Object.entries(customInputs).forEach(([k, v]) => {
        if (v && v.trim()) {
          const existing = finalSelections[k];
          if (Array.isArray(existing)) {
            finalSelections[k] = [...existing, `Anders: ${v.trim()}`];
          } else if (!existing) {
            finalSelections[k] = `Anders: ${v.trim()}`;
          }
        }
      });
      await workshopBuilderAPI.submit({
        template_id: selectedTpl.id,
        template_name: selectedTpl.name,
        selections: finalSelections,
        contact,
        notes,
      });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Versturen mislukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Renderers ──
  if (loadingList) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (step === 4) {
    return <SuccessView onRestart={() => { setStep(0); setSelectedTpl(null); setSelections({}); setContact({ name:'', email:'', phone:'', company:'' }); setNotes(''); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 py-8 px-4 text-slate-800" data-testid="workshop-builder-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
              <div className="text-xs text-slate-500">Workshop Builder</div>
            </div>
          </div>
          {selectedTpl && step > 0 && step < 4 && (
            <button
              onClick={() => { setStep(0); setSelectedTpl(null); setSelections({}); }}
              className="text-xs text-slate-500 hover:text-emerald-700 underline"
            >
              Andere workshop kiezen
            </button>
          )}
        </div>

        {/* Progress bar */}
        {step > 0 && step < 4 && (
          <ProgressBar step={step} totalSteps={3} />
        )}

        {/* Steps */}
        {step === 0 && (
          <PickTemplate templates={templates} onPick={handlePickTemplate} error={error} />
        )}

        {step === 1 && selectedTpl && (
          <BuildBlocks
            template={selectedTpl}
            selections={selections}
            setSelection={setSelection}
            toggleMulti={toggleMulti}
            customInputs={customInputs}
            setCustomInputs={setCustomInputs}
            onNext={handleNext}
            onBack={handleBack}
            error={error}
          />
        )}

        {step === 2 && (
          <ContactStep
            contact={contact}
            setContact={setContact}
            notes={notes}
            setNotes={setNotes}
            onNext={handleNext}
            onBack={handleBack}
            error={error}
          />
        )}

        {step === 3 && selectedTpl && (
          <ReviewStep
            template={selectedTpl}
            selections={selections}
            customInputs={customInputs}
            contact={contact}
            notes={notes}
            onSubmit={handleSubmit}
            onBack={handleBack}
            submitting={submitting}
            error={error}
          />
        )}

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-slate-500">
          <p>Vitaliteit als missie. © Zynq</p>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ───
function ProgressBar({ step, totalSteps }) {
  const labels = ['Bouw', 'Contact', 'Bevestig'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {labels.map((lbl, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <div key={lbl} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  done ? 'bg-emerald-600 text-white' :
                  active ? 'bg-emerald-600 text-white ring-4 ring-emerald-100' :
                  'bg-gray-200 text-slate-500'
                }`}>
                  {done ? <Check className="w-4 h-4" /> : idx}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${active || done ? 'text-emerald-700' : 'text-slate-400'}`}>{lbl}</span>
              </div>
              {i < labels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${done ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PickTemplate({ templates, onPick, error }) {
  return (
    <div data-testid="step-pick">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-slate-900">
          Bouw jouw <span className="text-emerald-600">ideale workshop</span>
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto">
          Kies een workshop-type om mee te starten. Vervolgens stel je zelf de onderdelen samen.
        </p>
      </div>

      {error && <p className="text-sm text-red-600 text-center mb-4">{error}</p>}

      {templates.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Er zijn momenteel geen workshops beschikbaar.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map(tpl => {
            const Icon = getIcon(tpl.icon);
            return (
              <button
                key={tpl.id}
                onClick={() => onPick(tpl)}
                data-testid={`template-${tpl.id}`}
                className="group text-left bg-white rounded-2xl border-2 border-transparent hover:border-emerald-500 shadow-sm hover:shadow-lg transition-all p-6 relative overflow-hidden"
                style={{ borderTopColor: tpl.color || '#10B981', borderTopWidth: '4px' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: `${tpl.color || '#10B981'}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: tpl.color || '#10B981' }} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-slate-900">{tpl.name}</h3>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{tpl.description}</p>
                <div className="flex items-center text-emerald-700 text-sm font-semibold">
                  Start bouwen <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BuildBlocks({ template, selections, setSelection, toggleMulti, customInputs, setCustomInputs, onNext, onBack, error }) {
  const Icon = getIcon(template.icon);
  return (
    <div data-testid="step-blocks">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
          style={{ background: `${template.color || '#10B981'}20`, color: template.color || '#10B981' }}>
          <Icon className="w-3.5 h-3.5" /> {template.name}
        </div>
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Stel jouw workshop samen</h2>
        {template.intro_text && <p className="text-slate-600 text-sm max-w-xl mx-auto">{template.intro_text}</p>}
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          {(template.blocks || []).map(block => (
            <BlockRenderer
              key={block.key}
              block={block}
              value={selections[block.key]}
              onChange={setSelection}
              onToggleMulti={toggleMulti}
              customInputs={customInputs}
              setCustomInputs={setCustomInputs}
            />
          ))}
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onNext} data-testid="btn-next-blocks" className="gap-2 bg-emerald-600 hover:bg-emerald-700">
          Volgende <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function BlockRenderer({ block, value, onChange, onToggleMulti, customInputs, setCustomInputs }) {
  const customVal = customInputs[block.key] || '';
  const setCustomVal = (v) => setCustomInputs(p => ({ ...p, [block.key]: v }));

  return (
    <div data-testid={`block-${block.key}`}>
      <Label className="text-base font-semibold flex items-center gap-1 text-slate-900">
        {block.label}
        {block.required && <span className="text-red-500">*</span>}
      </Label>
      {block.description && <p className="text-xs text-slate-500 mt-0.5 mb-3">{block.description}</p>}

      {block.type === 'single_choice' && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {(block.options || []).map(opt => {
            const v = opt.value || opt.label;
            const selected = value === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onChange(block.key, v)}
                data-testid={`opt-${block.key}-${v}`}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  selected
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                    {opt.description && <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>}
                  </div>
                  {selected && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {block.type === 'multi_choice' && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {(block.options || []).map(opt => {
            const v = opt.value || opt.label;
            const arr = Array.isArray(value) ? value : [];
            const selected = arr.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggleMulti(block.key, v)}
                data-testid={`opt-${block.key}-${v}`}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  selected
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                    selected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                  }`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                    {opt.description && <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {block.type === 'text' && (
        <Input
          value={value || ''}
          onChange={(e) => onChange(block.key, e.target.value)}
          placeholder={block.placeholder || ''}
          className="mt-3 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
          data-testid={`input-${block.key}`}
        />
      )}

      {block.type === 'textarea' && (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(block.key, e.target.value)}
          placeholder={block.placeholder || ''}
          rows={4}
          className="mt-3 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
          data-testid={`input-${block.key}`}
        />
      )}

      {block.type === 'number' && (
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(block.key, e.target.value)}
          placeholder={block.placeholder || ''}
          className="mt-3 max-w-xs bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
          data-testid={`input-${block.key}`}
        />
      )}

      {block.allow_custom && (block.type === 'single_choice' || block.type === 'multi_choice') && (
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Anders / eigen aanvulling</Label>
          <Input
            value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
            placeholder="Beschrijf je eigen wens..."
            className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
            data-testid={`custom-${block.key}`}
          />
        </div>
      )}
    </div>
  );
}

function ContactStep({ contact, setContact, notes, setNotes, onNext, onBack, error }) {
  return (
    <div data-testid="step-contact">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Jouw gegevens</h2>
        <p className="text-slate-600 text-sm">We nemen snel contact op om de workshop verder af te stemmen.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-4">
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><User className="w-3.5 h-3.5" /> Naam <span className="text-red-500">*</span></Label>
            <Input value={contact.name} onChange={(e) => setContact(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-name" />
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Building className="w-3.5 h-3.5" /> Bedrijf / organisatie</Label>
            <Input value={contact.company} onChange={(e) => setContact(p => ({ ...p, company: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-company" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Mail className="w-3.5 h-3.5" /> E-mail <span className="text-red-500">*</span></Label>
              <Input type="email" value={contact.email} onChange={(e) => setContact(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-email" />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Phone className="w-3.5 h-3.5" /> Telefoon</Label>
              <Input value={contact.phone} onChange={(e) => setContact(p => ({ ...p, phone: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" placeholder="+31 6 ..." data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><MessageSquare className="w-3.5 h-3.5" /> Aanvullende opmerkingen</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" placeholder="Specifieke wensen, vragen of context..." data-testid="contact-notes" />
          </div>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onNext} data-testid="btn-next-contact" className="gap-2 bg-emerald-600 hover:bg-emerald-700">
          Volgende <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function ReviewStep({ template, selections, customInputs, contact, notes, onSubmit, onBack, submitting, error }) {
  const formatVal = (block, v) => {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
    const labelFor = (val) => {
      const found = (block.options || []).find(o => (o.value || o.label) === val);
      return found ? found.label : val;
    };
    if (Array.isArray(v)) return v.map(labelFor).join(', ');
    return labelFor(v);
  };

  return (
    <div data-testid="step-review">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Bevestig je aanvraag</h2>
        <p className="text-slate-600 text-sm">Controleer de samenstelling en verstuur.</p>
      </div>

      <div className="space-y-4">
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-emerald-700 mb-3">Workshop</h3>
            <div className="text-sm font-semibold text-slate-900">{template.name}</div>
            {(template.blocks || []).map(block => {
              const val = formatVal(block, selections[block.key]);
              const custom = customInputs[block.key];
              if (!val && !custom) return null;
              return (
                <div key={block.key} className="mt-3 pt-3 border-t border-slate-100 first-of-type:border-t-0 first-of-type:pt-3">
                  <div className="text-xs text-slate-500">{block.label}</div>
                  <div className="text-sm font-medium mt-0.5 text-slate-800">
                    {val}
                    {custom && <span className="text-slate-500 italic"> {val ? '+ ' : ''}Anders: {custom}</span>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-emerald-700 mb-3">Contactgegevens</h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Naam:</span> <span className="font-semibold text-slate-900">{contact.name}</span></div>
              {contact.company && <div><span className="text-slate-500">Bedrijf:</span> <span className="font-semibold text-slate-900">{contact.company}</span></div>}
              <div><span className="text-slate-500">E-mail:</span> <span className="font-semibold text-slate-900">{contact.email}</span></div>
              {contact.phone && <div><span className="text-slate-500">Telefoon:</span> <span className="font-semibold text-slate-900">{contact.phone}</span></div>}
            </div>
            {notes && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500">Opmerkingen</div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap text-slate-800">{notes}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} disabled={submitting} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onSubmit} disabled={submitting} data-testid="btn-submit" className="gap-2 bg-emerald-600 hover:bg-emerald-700 min-w-[180px]">
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Versturen...</>) : (<>Aanvraag versturen <Send className="w-4 h-4" /></>)}
        </Button>
      </div>
    </div>
  );
}

function SuccessView({ onRestart }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 flex items-center justify-center p-4 text-slate-800" data-testid="step-success">
      <Card className="max-w-md w-full border-slate-200 shadow-lg bg-white">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Bedankt!</h2>
          <p className="text-slate-600 text-sm">
            We hebben je workshop aanvraag goed ontvangen.<br />
            Een van onze vitaliteits-experts neemt binnen 1-2 werkdagen contact met je op.
          </p>
          <div className="pt-3">
            <Button onClick={onRestart} variant="outline" className="gap-2">
              <Sparkles className="w-4 h-4" /> Nog een workshop bouwen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
